import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import type { MessageResponse } from "../../../api/generated";
import { MessageBubble } from "./MessageBubble";
import { StreamingMessage } from "./StreamingMessage";
import { ThinkingIndicator } from "./ThinkingIndicator";

interface MessageListProps {
  messages: MessageResponse[];
  isStreaming: boolean;
  streamingContent: string;
  /** Show the "working" indicator while a response is generating but no content has streamed yet. */
  isThinking?: boolean;
  thinkingLabel?: string;
  thinkingDetail?: string;
}

export function MessageList({
  messages,
  isStreaming,
  streamingContent,
  isThinking = false,
  thinkingLabel = "Working…",
  thinkingDetail,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll on new messages, streaming content, or when thinking starts
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, streamingContent, isThinking]);

  return (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        py: 2,
      }}
    >
      {messages.map((msg) => (
        <MessageBubble key={msg.id} message={msg} />
      ))}
      {streamingContent &&
        (isStreaming || messages[messages.length - 1]?.role !== 'assistant') && (
          <StreamingMessage content={streamingContent} />
        )}
      {isThinking && !streamingContent && (
        <ThinkingIndicator label={thinkingLabel} detail={thinkingDetail} />
      )}
      <div ref={bottomRef} />
    </Box>
  );
}
